/**
 * Interview status — single source of truth.
 *
 * These helpers derive the interview Type chip + Status badges shown on the
 * Technical/Behavioral talent LIST pages (the shared CandidatesTable) AND, as of
 * the timeline enhancement, on the profile Timeline feed. They were previously
 * private to `talentTransformer.ts`; they were extracted here verbatim so the
 * list and the profile can never diverge. `talentTransformer.ts` imports them
 * back, so list output stays byte-identical.
 *
 * Keep the emoji / color / label / branch order EXACTLY as-is — the list's
 * `status_html` is compared byte-for-byte in tests.
 */
import dayjs from "dayjs";

// ─── Types ────────────────────────────────────────────────────────
export type InterviewLinkAttempt = {
  /** talent.interview_attempts.id — natural key for M2 interview predictions. */
  id?: string;
  status?: "success" | "failure" | string;
  client_exited?: boolean;
  screen_exit_count?: number;
  started_at?: string | null;
  completed_at?: string | null;
  score?: {
    scores?: {
      notes_html?: string | null;
      communication?: number | null;
      technical?: number | null;
      overall?: number | null;
      [k: string]: any;
    };
    [k: string]: any;
  } | null;
};

export type InterviewLink = {
  created_at: string;
  link_sent_on?: string | null;
  link_visited_on?: string | null;
  calendly_scheduling_url?: string | null;
  calendly_event_uri?: string | null;
  interview_link_attempts?: InterviewLinkAttempt[];
};

export type LatestAiScreeningStatus = {
  link: InterviewLink;
  date: string | null;
  status: string | null;
  /**
   * Every attempt across ALL of the profile's links, newest link first. The
   * status above is the newest link's, but a newer link can be empty (a re-invite
   * not taken yet, or a duplicate sent seconds later) while the interview that
   * was actually recorded sits on an older one. Playback reads this list.
   */
  attempts: InterviewLinkAttempt[];
};

/**
 * The subset of a talent/profile row that the status helpers read. Both the
 * list `RawTalentRow` and the profile-derived schedule rows are structurally
 * assignable to this.
 */
export type InterviewStatusRow = {
  ai_screening?: any;
  ai_screening_status?: string | null;
  has_calendly_booking?: boolean | null;
  calendly_booking_at?: string | null;
  manual_interview_status?: string | null;
  manual_next_interview_at?: string | null;
  manual_past_due_at?: string | null;
  interview_links?: InterviewLink[] | null;
  // talent.interviews.interview_status of THIS round's row. Only the profile timeline passes it;
  // 'skipped' = the round was skipped (Skip First Round) and wins over every other status.
  round_status?: string | null;
};

type AiEvent = { type: string; status: string; date: Date };

export function parseLatestAi(row: InterviewStatusRow) {
  const links: InterviewLink[] = Array.isArray(row.interview_links)
    ? row.interview_links
    : [];
  if (!links.length) return null;

  const latestLink = links.reduce((a, b) =>
    new Date(b.created_at) > new Date(a.created_at) ? b : a,
  );

  const events: AiEvent[] = [];

  (latestLink.interview_link_attempts || []).forEach((a) => {
    const baseDate = a.completed_at || a.started_at || latestLink.link_sent_on;
    if (!baseDate) return;

    if (a.status === "success") {
      events.push({ type: "Status", status: "Completed", date: new Date(baseDate) });
    }
    if (a.status === "failure" || a.status === "unknown") {
      events.push({ type: "Status", status: "Incomplete", date: new Date(baseDate) });
    }
    if (a.client_exited === true || (a.screen_exit_count ?? 0) > 2) {
      events.push({ type: "Status", status: "Exited", date: new Date(baseDate) });
    }

    if (a.started_at) {
      events.push({ type: "Started", status: "Started", date: new Date(a.started_at) });
    }
    if (a.completed_at) {
      events.push({ type: "Completed", status: "Completed", date: new Date(a.completed_at) });
    }
  });

  if (latestLink.link_visited_on) {
    events.push({ type: "Visited", status: "Visited", date: new Date(latestLink.link_visited_on) });
  }
  if (latestLink.link_sent_on) {
    events.push({ type: "Sent", status: "Sent", date: new Date(latestLink.link_sent_on) });
  }

  if (!events.length) {
    return {
      latestLink,
      latestEvent: null as AiEvent | null,
      dateText: null,
      statusText: null,
    };
  }

  const priority = ["Status", "Completed", "Started", "Visited", "Sent"];
  const latestEvent = events.sort(
    (a, b) =>
      priority.indexOf(a.type) - priority.indexOf(b.type) ||
      b.date.getTime() - a.date.getTime(),
  )[0];

  return {
    latestLink,
    latestEvent,
    dateText: dayjs(latestEvent.date).format("MMMM D, YYYY hh:mm:ss A"),
    statusText: latestEvent.status,
  };
}

export function getLatestAIScreeningStatus(
  row: InterviewStatusRow,
): LatestAiScreeningStatus | null {
  const parsed = parseLatestAi(row);
  if (!parsed) return null;

  return {
    link: parsed.latestLink,
    date: parsed.dateText,
    status: parsed.statusText,
    attempts: allAiAttempts(row),
  };
}

/** Attempts from every link, newest link first (each link's own list is already newest-first). */
export function allAiAttempts(row: InterviewStatusRow): InterviewLinkAttempt[] {
  const links: InterviewLink[] = Array.isArray(row.interview_links) ? row.interview_links : [];
  return [...links]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .flatMap((l) => l.interview_link_attempts || []);
}

// ─── Interview status helpers ──────────────────────────────────────

export function statusBadge(emoji: string, label: string, color: string, sub?: string): string {
  let html = `<div style="display:flex;align-items:center;gap:6px;color:${color};font-weight:600;">${emoji} ${label}</div>`;
  if (sub) html += `<div style="font-size:11px;color:#6b7280;">${sub}</div>`;
  return html;
}

export function getUnifiedStatusHtml(row: InterviewStatusRow, interviewType?: string): string {
  if (row.round_status === "skipped") {
    return `<div style="display:flex;flex-direction:column;gap:2px;">${statusBadge("⏭", "Skipped", "#6b7280", "Sent straight to the final round")}</div>`;
  }
  const isAi = row.ai_screening === true && interviewType !== "behavioral";

  if (isAi) {
    const aiStatus = (row as any).ai_screening_status as string | undefined;
    const parsed = parseLatestAi(row);
    const badges: string[] = [];

    // Primary status from attempts
    if (parsed?.latestEvent) {
      const s = parsed.latestEvent.status;
      const dateStr = parsed.latestEvent.date.toLocaleString();
      if (s === "Completed") {
        badges.push(statusBadge("🤖", "Completed", "#059669", `at ${dateStr}`));
      } else if (s === "Incomplete" || s === "Exited") {
        badges.push(statusBadge("🤖", "Incomplete", "#dc2626", `at ${dateStr}`));
      } else if (s === "Started") {
        badges.push(statusBadge("🤖", "Started", "#b45309", `at ${dateStr}`));
      } else if (s === "Visited") {
        badges.push(statusBadge("👁️", "Visited", "#15803d", `at ${dateStr}`));
      } else if (s === "Sent") {
        badges.push(statusBadge("📨", "Link Sent", "#2563eb", `at ${dateStr}`));
      }
    }

    // Overlay: manual Calendly booking (AI candidate who booked a manual slot)
    if (row.has_calendly_booking && row.calendly_booking_at) {
      badges.push(statusBadge("📅", "Scheduled", "#2563eb", `at ${new Date(row.calendly_booking_at).toLocaleString()}`));
    }

    // Overlay: Calendly scheduling status
    if (aiStatus === "scheduled") {
      badges.push(statusBadge("📅", "Scheduled", "#2563eb"));
    } else if (aiStatus === "missed") {
      badges.push(statusBadge("⚠️", "Missed Interview", "#dc2626"));
    } else if (aiStatus === "canceled") {
      badges.push(statusBadge("❌", "Canceled", "#dc2626"));
    } else if (parsed?.latestLink?.calendly_scheduling_url && !parsed?.latestLink?.calendly_event_uri) {
      badges.push(statusBadge("🕐", "Awaiting Scheduling", "#92400E"));
    }

    if (badges.length === 0) {
      badges.push(statusBadge("🤖", "AI screening pending", "#2563eb"));
    }

    return `<div style="display:flex;flex-direction:column;gap:4px;">${badges.join("")}</div>`;
  }

  if (row.manual_interview_status === "scheduled") {
    return `<div style="display:flex;flex-direction:column;gap:2px;">${statusBadge("📅", "Scheduled", "#2563eb", `at ${row.manual_next_interview_at ? new Date(row.manual_next_interview_at).toLocaleString() : ""}`)}</div>`;
  }

  if (row.manual_interview_status === "past_due") {
    return `<div style="display:flex;flex-direction:column;gap:2px;">${statusBadge("⏰", "Past due", "#dc2626", `at ${row.manual_past_due_at ? new Date(row.manual_past_due_at).toLocaleString() : ""}`)}</div>`;
  }

  return `<div style="display:flex;flex-direction:column;gap:2px;">${statusBadge("⏺", "No interview scheduled", "#6b7280")}</div>`;
}

export function getUnifiedStatusRaw(row: InterviewStatusRow, interviewType?: string): string {
  if (row.round_status === "skipped") return "Skipped";
  const isAi = row.ai_screening === true && interviewType !== "behavioral";

  if (isAi) {
    const aiStatus = (row as any).ai_screening_status as string | undefined;
    if (aiStatus === "scheduled") return "AI interview scheduled";
    if (aiStatus === "missed") return "AI interview missed";
    if (aiStatus === "canceled") return "AI interview canceled";

    const parsed = parseLatestAi(row);
    if (!parsed || !parsed.latestEvent) return "AI screening pending";

    const s = parsed.latestEvent.status;
    if (s === "Completed") return "AI screening completed";
    if (s === "Started") return "AI screening started";
    if (s === "Visited") return "AI screening visited";
    if (s === "Incomplete" || s === "Failed" || s === "Exited") return "AI interview incomplete";
    return "AI screening sent";
  }

  if (row.manual_interview_status === "scheduled") return "Manual interview scheduled";
  if (row.manual_interview_status === "past_due") return "Manual interview past due";
  return "No interview scheduled";
}
